// src/components/About.js
import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";

function About() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C97A;
          --dark: #0A0A0A;
          --charcoal: #141414;
          --cream: #F5F0E8;
        }

        .about-root {
          font-family: 'Jost', sans-serif;
          background: var(--dark);
          color: #fff;
          min-height: 100vh;
        }

        /* ── LEADER HERO ── */
        .leader-hero {
          background: linear-gradient(160deg, #0f0d07 0%, #1a1500 60%, #0A0A0A 100%);
          border-bottom: 1px solid rgba(201,168,76,0.2);
          padding: 5rem 1.5rem 4rem;
        }
        .leader-photo-wrap {
          position: relative;
          display: inline-block;
          margin-bottom: 2rem;
        }
        .leader-photo {
          width: 180px;
          height: 180px;
          object-fit: cover;
          border-radius: 50%;
          border: 3px solid var(--gold);
          box-shadow: 0 0 0 8px rgba(201,168,76,0.12), 0 20px 60px rgba(0,0,0,0.6);
          display: block;
        }
        .leader-badge {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: var(--gold);
          color: #0A0A0A;
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          padding: 0.25rem 0.6rem;
          border-radius: 30px;
          white-space: nowrap;
        }
        .leader-eyebrow {
          font-size: 0.7rem;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.5rem;
        }
        .leader-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.2rem, 5vw, 3.6rem);
          font-weight: 700;
          line-height: 1.05;
          color: #fff;
          margin-bottom: 0.3rem;
        }
        .leader-title {
          font-size: 0.78rem;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.45);
          margin-bottom: 1.8rem;
        }
        .leader-divider {
          width: 48px;
          height: 2px;
          background: var(--gold);
          margin: 0 auto 1.8rem;
        }
        .about-root .leader-hero .text-start .leader-divider {
          margin: 0 0 1.8rem;
        }
        .leader-quote {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.05rem, 2.5vw, 1.3rem);
          font-style: italic;
          line-height: 1.85;
          color: rgba(255,255,255,0.82);
          max-width: 680px;
        }
        .leader-quote strong {
          color: var(--gold-light);
          font-style: normal;
          font-weight: 600;
        }

        /* ── CONTENT SECTIONS ── */
        .about-content {
          padding: 5rem 0;
          background: var(--dark);
        }
        .about-block {
          padding: 0 0 3.5rem;
        }
        .about-block:last-child { padding-bottom: 0; }

        .block-label {
          font-size: 0.65rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.6rem;
        }
        .block-heading {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.7rem, 4vw, 2.4rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 1.2rem;
          line-height: 1.15;
        }
        .block-line {
          width: 40px;
          height: 2px;
          background: var(--gold);
          margin-bottom: 1.4rem;
        }
        .block-text {
          font-size: 1rem;
          font-weight: 300;
          line-height: 1.9;
          color: rgba(255,255,255,0.72);
          max-width: 680px;
        }

        /* ── DIVIDER ── */
        .gold-rule {
          border: none;
          border-top: 1px solid rgba(201,168,76,0.2);
          margin: 0;
        }

        /* Values grid */
        .values-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }
        .value-card {
          background: rgba(201,168,76,0.06);
          border: 1px solid rgba(201,168,76,0.18);
          border-radius: 6px;
          padding: 1.8rem 1.5rem;
          text-align: center;
          transition: border-color 0.3s, transform 0.3s;
        }
        .value-card:hover {
          border-color: rgba(201,168,76,0.5);
          transform: translateY(-4px);
        }
        .value-icon {
          font-size: 2rem;
          margin-bottom: 0.8rem;
          display: block;
        }
        .value-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--gold-light);
          margin-bottom: 0.4rem;
        }
        .value-desc {
          font-size: 0.83rem;
          color: rgba(255,255,255,0.55);
          line-height: 1.6;
        }

        @media (max-width: 767px) {
          .leader-hero { text-align: center; }
          .leader-divider { margin: 0 auto 1.8rem; }
          .block-text { max-width: 100%; }
        }
      `}</style>

      <div className="about-root">

        {/* ── TEAM LEADER HERO ── */}
        <section className="leader-hero">
          <div className="container">
            <div className="row align-items-center gy-4">

              {/* Photo */}
              <div className="col-12 col-md-auto text-center">
                <div className="leader-photo-wrap">
                  <img
                    src="/images/teamleader.jpeg"
                    alt="Edwin Obeni — Team Leader"
                    className="leader-photo"
                  />
                  <span className="leader-badge">Team Leader</span>
                </div>
              </div>

              {/* Text */}
              <div className="col-12 col-md">
                <p className="leader-eyebrow">Meet</p>
                <h1 className="leader-name">Edwin Obeni</h1>
                <p className="leader-title">
                  Team Leader · Nairobi Ultimate Experience City Tours &amp; Adventure
                </p>
                <div className="leader-divider" />
                <p className="leader-quote">
                  We craft <strong>elite, immersive journeys</strong> that showcase Nairobi beyond
                  the ordinary — where culture, energy, and adventure meet. Our focus is simple:
                  precision, excellence, and <strong>unforgettable experiences</strong>.
                  <br /><br />
                  I'm building a brand that doesn't follow standards — it sets them.
                  Nairobi isn't just a destination to us; <strong>it's an experience, elevated.</strong>
                </p>
              </div>

            </div>
          </div>
        </section>

        <hr className="gold-rule" />

        {/* ── ABOUT CONTENT ── */}
        <section className="about-content">
          <div className="container">

            <div className="about-block">
              <p className="block-label">Our Purpose</p>
              <h2 className="block-heading">Why We Exist</h2>
              <div className="block-line" />
              <p className="block-text">
                At Nairobi's Ultimate Experience, our mission is to ensure that every visitor
                discovers the city's vibrant culture, rich history, and stunning landscapes in a
                way that leaves an indelible mark on their heart. Founded in 2024 by passionate
                city walk tour guides, we saw an opportunity to elevate the tourist experience —
                transforming every visit into an unforgettable journey.
              </p>
            </div>

            <hr className="gold-rule" style={{ marginBottom: "3.5rem" }} />

            <div className="about-block">
              <p className="block-label">Our Approach</p>
              <h2 className="block-heading">How We Do It</h2>
              <div className="block-line" />
              <p className="block-text">
                Our experienced and knowledgeable guides are the heartbeat of Nairobi's Ultimate
                Experience. With deep roots in the streets of Nairobi, they bring the city to
                life — sharing local insights, hidden gems, and stories you won't find in any
                guidebook. We craft personalised tours that cater to your interests and curiosity,
                whether it's exploring bustling markets, tranquil parks, historic landmarks, or the
                vibrant art scene.
              </p>
            </div>

            <hr className="gold-rule" style={{ marginBottom: "3.5rem" }} />

            <div className="about-block">
              <p className="block-label">Our Offering</p>
              <h2 className="block-heading">What We Offer</h2>
              <div className="block-line" />
              <p className="block-text">
                We offer a range of meticulously planned tours designed to immerse you in the
                essence of Nairobi — from downtown walking tours and historical &amp; cultural
                journeys to thrilling excursions and authentic community visits.
              </p>

              <div className="values-grid">
                {[
                  { icon: "🚶", title: "City Walk Tours", desc: "Navigate Nairobi's vibrant downtown with expert guides." },
                  { icon: "🏛️", title: "Historical & Cultural", desc: "Uncover stories woven into landmarks and heritage sites." },
                  { icon: "🌄", title: "Excursions", desc: "Day trips beyond the city to Kenya's breathtaking scenery." },
                  { icon: "🤝", title: "Slum Tours", desc: "Authentic, respectful community experiences with local guides." },
                ].map((v, i) => (
                  <div key={i} className="value-card">
                    <span className="value-icon">{v.icon}</span>
                    <div className="value-title">{v.title}</div>
                    <div className="value-desc">{v.desc}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>

      </div>
    </>
  );
}

export default About;
